import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./components/Login";
import { FEATURES } from "./config/features";
import Portfolio from "./components/Portfolio";
import InspiyahStore from "./components/InspiyahStore";
import InspiyahJourney from "./components/InspiyahJourney";
import SocialMediaHub from "./components/SocialMediaHub";
import Donations from "./components/Donations";
import TeacherReferrals from "./components/TeacherReferrals";
import ArticlePage from "./components/ArticlePage";
import AdminLogin from "./components/admin/AdminLogin";
import AdminDashboard from "./components/admin/AdminDashboard";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import AIUsagePolicy from "./components/AIUsagePolicy";
import Disclaimer from "./components/Disclaimer";
import DataProcessingAddendum from "./components/DataProcessingAddendum";
import AnalyticsStats from "./components/AnalyticsStats";
import DNFMinistryApp from "./dnfministry";
import TyronApp from "./tyron";
import ColoringBookFunnel from "./components/ColoringBookFunnel";
import InspiyahCulture from "./components/InspiyahCulture";
import AECulturePostB from "./components/AE_CulturePostB";
import InspiyahMain from "./components/InspiyahMain";
import BookOfColox from "./components/BookOfColox";
import BookOfColorz from "./components/BookOfColorz";
import ColoringExperienceGuide from "./components/ColoringExperienceGuide";
import FAQPage from "./components/FAQPage";
import ArticlePreviewBoard from "./components/ArticlePreviewBoard";
import QuickLinks from "./components/QuickLinks";
import CheckoutSuccess from "./components/CheckoutSuccess";
import OrderStatus from "./components/OrderStatus";
import AdminFulfillment from "./components/AdminFulfillment";
import WorldOfInspiyah from "./components/WorldOfInspiyah";
import ComingSoon from "./components/ComingSoon";

/**
 * INSPIYAH Application Router
 * 
 * Routes are feature-gated based on environment:
 * - Production (MVP): Only Module 1 features (Home, About, Storyteller, Skills, Experience, Projects)
 * - Test/Development: All features enabled
 * 
 * Feature flags controlled by /src/config/features.js
 */

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const hostname = window.location.hostname;
  const isInspiyahDomain = hostname.includes('inspiyah.net') || hostname.includes('inspiyah.club') || hostname.includes('inspiyah.com');
  const isSeedzDomain = hostname.includes('seedz.bookofcolorz');

  return (
    <div className="App">
      <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Auth */}
          <Route path="/login" element={<Login />} />
          {/* Domain-based root routing */}
          {isSeedzDomain ? (
            <Route path="/" element={<ComingSoon />} />
          ) : isInspiyahDomain ? (
            <Route path="/" element={<InspiyahMain />} />
          ) : (
            <Route path="/" element={<Portfolio />} />
          )}

          {/* INSPIYAH MAIN - always accessible on its own route too */}
          <Route path="/inspiyah" element={<InspiyahMain />} />
          
          {/* Feature-gated routes */}
          {FEATURES.SHOP && <Route path="/inspiyah-culture" element={<InspiyahCulture />} />}
          <Route path="/inspiyah-culture-post-b" element={<AECulturePostB />} />
          {FEATURES.SHOP && <Route path="/tyron/*" element={<TyronApp />} />}
          {FEATURES.SHOP && <Route path="/dnfministry/*" element={<DNFMinistryApp />} />}
          {FEATURES.SHOP && <Route path="/coloringbook" element={<ColoringBookFunnel />} />}
          <Route path="/book-of-colox" element={<BookOfColox />} />
          <Route path="/book-of-colorz" element={<BookOfColorz />} />
          <Route path="/book-of-colorz-test" element={<BookOfColorz />} />
          <Route path="/coloring-guide" element={<ColoringExperienceGuide />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/preview-articles" element={<ProtectedRoute><ArticlePreviewBoard /></ProtectedRoute>} />
          <Route path="/quicklinks" element={<ProtectedRoute><QuickLinks /></ProtectedRoute>} />
          <Route path="/book-of-colox/success" element={<CheckoutSuccess />} />
          <Route path="/book-of-colorz/success" element={<CheckoutSuccess />} />
          <Route path="/book-of-colorz/order-status" element={<OrderStatus />} />
          <Route path="/admin/fulfillment" element={<ProtectedRoute><AdminFulfillment /></ProtectedRoute>} />
          <Route path="/world-of-inspiyah" element={<WorldOfInspiyah />} />
          <Route path="/coming-soon" element={<ComingSoon />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/ai-usage-policy" element={<AIUsagePolicy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/data-processing-addendum" element={<DataProcessingAddendum />} />
          <Route path="/book-of-colorz/privacy-policy" element={<PrivacyPolicy brand="boc" />} />
          <Route path="/book-of-colorz/terms-of-service" element={<TermsOfService brand="boc" />} />
          <Route path="/book-of-colorz/ai-usage-policy" element={<AIUsagePolicy brand="boc" />} />
          <Route path="/book-of-colorz/disclaimer" element={<Disclaimer brand="boc" />} />
          <Route path="/book-of-colorz/data-processing-addendum" element={<DataProcessingAddendum brand="boc" />} />
          <Route path="/analytics" element={<AnalyticsStats />} />
          
          {/* ❌ FUTURE ROUTES - Feature-Gated */}
          
          {/* E-Commerce Store */}
          {FEATURES.SHOP && (
            <Route path="/store" element={<InspiyahStore />} />
          )}
          
          {/* Content Hub & Blog */}
          {FEATURES.INSPIYAH_JOURNEY && (
            <>
              <Route path="/journey" element={<InspiyahJourney />} />
              <Route path="/article/:slug" element={<ArticlePage />} />
            </>
          )}
          
          {/* Community Features */}
          {FEATURES.SOCIAL_MEDIA_HUB && (
            <Route path="/social" element={<SocialMediaHub />} />
          )}
          
          {FEATURES.DONATIONS && (
            <Route path="/donations" element={<Donations />} />
          )}
          
          {FEATURES.TEACHER_REFERRALS && (
            <Route path="/referrals" element={<TeacherReferrals />} />
          )}
          
          {/* Admin Dashboard */}
          {FEATURES.ADMIN_DASHBOARD && (
            <>
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            </>
          )}
          
          {/* Fallback: Redirect to home if route disabled or not found */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;